import {
  type AuthCredential,
  type User,
  type UserCredential,
  linkWithCredential,
} from 'firebase/auth'
import { toast } from 'sonner'
import logger from '~/src/lib/logger'

/**
 * A social sign-in that collided with an existing account, waiting to be
 * linked once the person signs in the way they did before.
 *
 * With "one account per email" on (our Firebase setting), signing in with
 * Facebook using an email that already belongs to a Google/Apple account fails
 * with `auth/account-exists-with-different-credential`. Firebase's email
 * enumeration protection also hides WHICH provider owns the email, so we can't
 * name it — we can only ask the person to sign in as usual, then attach the
 * held credential to that account.
 *
 * This is also the single source of truth for the "welcome back" step, so the
 * step survives the sign-in buttons unmounting (e.g. during the phone code
 * screen). `credential`/`email` can be missing when Firebase didn't return
 * them: the step still shows, there is just nothing to link.
 *
 * In memory only: every sign-in path (popup, phone code) completes in the same
 * document, and a credential must never outlive the tab. Browser-only — a
 * module singleton on the server would be shared across requests.
 */
export interface PendingLink {
  /** The login that collided, e.g. `facebook.com`. */
  providerId: string
  credential?: AuthCredential
  email?: string
  createdAt: number
}

const PROVIDER_LABELS: Record<string, string> = {
  'google.com': 'Google',
  'apple.com': 'Apple',
  'facebook.com': 'Facebook',
}

export const providerLabel = (providerId?: string) =>
  (providerId && PROVIDER_LABELS[providerId]) || 'this login'

const PENDING_LINK_TTL_MS = 10 * 60 * 1000

let pendingLink: PendingLink | null = null
const listeners = new Set<() => void>()

const update = (next: PendingLink | null) => {
  pendingLink = next
  listeners.forEach((listener) => listener())
}

export const subscribePendingLink = (listener: () => void) => {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

export const setPendingLink = ({
  providerId,
  credential,
  email,
}: Omit<PendingLink, 'createdAt'>) => {
  if (typeof window === 'undefined') return
  update({ providerId, credential, email, createdAt: Date.now() })
}

export const clearPendingLink = () => {
  if (pendingLink) update(null)
}

export const getPendingLink = (): PendingLink | null => {
  if (!pendingLink) return null
  if (Date.now() - pendingLink.createdAt > PENDING_LINK_TTL_MS) {
    pendingLink = null
    return null
  }
  return pendingLink
}

/**
 * Attach the held credential to `user` if the emails match. Never throws: a
 * failed link must not turn a successful sign-in into a failed one.
 *
 * The email check stops a credential being attached to a different person's
 * account (e.g. a shared device where someone else signs in next).
 */
export const linkPendingCredential = async (
  user: User | null | undefined
): Promise<UserCredential | undefined> => {
  const pending = getPendingLink()
  if (!pending || !user || user.isAnonymous) return undefined

  const label = providerLabel(pending.providerId)

  if (
    !pending.credential ||
    !pending.email ||
    user.email?.toLowerCase() !== pending.email.toLowerCase()
  ) {
    clearPendingLink()
    if (pending.credential) {
      toast(`${label} wasn't connected`, {
        description: `You signed in to a different account than your ${label} email.`,
      })
    }
    return undefined
  }

  try {
    const result = await linkWithCredential(user, pending.credential)
    clearPendingLink()
    toast.success(`${label} connected`, {
      description: 'Next time, you can sign in with either.',
    })
    return result
  } catch (error: any) {
    clearPendingLink()
    // `provider-already-linked`: this account already has one of that provider.
    // `credential-already-in-use`: the social account is linked elsewhere.
    logger.error('Error linking pending credential', {
      code: error?.code,
      providerId: pending.providerId,
    })
    return undefined
  }
}
